import {createActions} from 'redux-actions';
import fetchJson, {postJson, putJson, deleteJson, postForm} from '../fetchJson';
import {push} from 'connected-react-router';
import handleError from '../handleError';
import uuidv4 from 'uuid/v4';
import ReactGA from 'react-ga';
import {uiActions} from '../chrome';
import {updateExceededLimit} from '../user/session.actions';

const actions = createActions(
	'COMMUNITY_LOADING',
	'COMMUNITY_LOADED',
	'COMMUNITY_MEMBERS_LOADED',
	'COMMUNITY_JOINED',
	'COMMUNITY_EDITING_FIELD',
	'COMMUNITY_MEMBERSHIP_APPROVED',
	'COMMUNITY_MEMBERSHIP_REJECTED',
	'COMMUNITY_SET_TYPE',
	'COMMUNITY_SET_NOTICES_ENABLED',
	'COMMUNITY_SET_NOTICES_ALLOW_ALL',
	'COMMUNITY_UPDATE_ABOUT',
	'COMMUNITY_UPDATE_BRIEF',
	'COMMUNITY_UPDATE_NAME',
	'COMMUNITY_UPDATE_CATEGORY',
	'COMMUNITY_UPDATE_LOCATION',
	'COMMUNITY_COMMIT_CONTACTS',
	'COMMUNITY_EDIT_CONTACTS',
	'COMMUNITY_CHANGE_EDIT_CONTACTS',
	'COMMUNITY_CANCEL_CONTACTS',
	'COMMUNITY_DRAG_OVER_BACKGROUND_IMAGE',
	'COMMUNITY_DRAG_OUT_BACKGROUND_IMAGE',
	'COMMUNITY_UPDATING_BACKGROUND_IMAGE',
	'COMMUNITY_UPDATE_BACKGROUND_IMAGE',
	'COMMUNITY_ADDING_PROFILE_IMAGE',
	'COMMUNITY_ADDED_PROFILE_IMAGE',
	'COMMUNITY_DRAG_OVER_PROFILE_IMAGE',
	'COMMUNITY_DRAG_OUT_PROFILE_IMAGE',
	'COMMUNITY_DELETE_IMAGE',
	'COMMUNITY_UPDATE_FIELD',
	'COMMUNITY_ADMIN_APPLY',
	'COMMUNITY_ADMIN_REVOKE',
	'COMMUNITY_REMOVE',
	'COMMUNITY_MUTE',
	'COMMUNITY_RESET_COLOUR',
	'COMMUNITY_SET_IMGS'
);

export const retrieveCommunity = uniqueName => (dispatch, getState) => {
	const existingCommunity = getState().community;
	if (existingCommunity && existingCommunity.uniqueName === uniqueName) {
		// Already loaded, do nothing.
		return;
	}

	dispatch(actions.communityLoading(uniqueName));

	fetchJson('/api/community/?profileUniqueName=' + uniqueName)
		.then(community => dispatch(actions.communityLoaded(community)))
		.catch(handleError(dispatch));

	fetchJson('/api/providers/?communityUniqueName=' + uniqueName)
		.then(members => dispatch(actions.communityMembersLoaded(members)))
		.catch(handleError(dispatch));
};

export const joinCommunity = uuid => (dispatch, getState) => {
	const userUuid = getState().session.current.user.uuid;
	const {community} = getState();
	const communityUuid = uuid || community.uuid;

	ReactGA.event({
		category: 'Community',
		action: 'Join'
	});

	dispatch(
		actions.communityJoined({
			...getState().session.current.provider,
			membership: {
				status: community && community.type === 'open' ? 'approved' : 'requested', // TODO this should be based on the community type
				admin: false
			}
		})
	);

	postJson(`/api/community/${communityUuid}/members/${userUuid}`, {})
		.then(() => dispatch(updateExceededLimit()))
		.catch(e =>
			e && e.name === '400'
				? console.warn('Ignoring community membership request, already a member.')
				: handleError(dispatch)(e)
		);
};

export const approveMembership = userUuid => (dispatch, getState) => {
	const communityUuid = getState().community.uuid;

	ReactGA.event({
		category: 'Community',
		action: 'Approve membership'
	});

	dispatch(actions.communityMembershipApproved(userUuid));

	putJson(`/api/community/${communityUuid}/members/${userUuid}`, {}).catch(handleError(dispatch));
};

export const rejectMembership = userUuid => (dispatch, getState) => {
	const communityUuid = getState().community.uuid;

	ReactGA.event({
		category: 'Community',
		action: 'Reject membership'
	});

	dispatch(actions.communityMembershipRejected(userUuid));

	deleteJson(`/api/community/${communityUuid}/members/${userUuid}`, {}).catch(
		handleError(dispatch)
	);
};

export const removeMembership = () => (dispatch, getState) => {
	const {session, community} = getState();

	if (!community) throw Error('Expected community to be open to leave community');
	if (!session || !session.current || !session.current.user)
		throw Error('Expected user session to leave community');

	ReactGA.event({
		category: 'Community',
		action: 'Leave community'
	});

	dispatch(actions.communityMembershipRejected(session.current.user.uuid));

	deleteJson(`/api/community/${community.uuid}/members/${session.current.user.uuid}`, {}).catch(
		handleError(dispatch)
	);

	setTimeout(() => dispatch(updateExceededLimit()), 500);
};

export const applyAdmin = (userUuid, isAdmin = true) => (dispatch, getState) => {
	const communityUuid = getState().community.uuid;

	ReactGA.event({
		category: 'Community',
		action: 'Apply administrator'
	});

	if (isAdmin) {
		dispatch(actions.communityAdminApply(userUuid));
		putJson(`/api/community/${communityUuid}/admin/${userUuid}`, {}).catch(handleError(dispatch));
	} else {
		dispatch(actions.communityAdminRevoke(userUuid));
		deleteJson(`/api/community/${communityUuid}/admin/${userUuid}`, {}).catch(
			handleError(dispatch)
		);
	}
};

export const toggleType = () => (dispatch, getState) => {
	const oldType = getState().community.type;
	const type = oldType === 'open' ? 'closed' : 'open';

	ReactGA.event({
		category: 'Community',
		action: type === 'open' ? 'Open type' : 'Closed type'
	});

	dispatch(actions.communitySetType(type));

	putJson(`/api/community/${getState().community.uuid}`, {type}).catch(handleError(dispatch));
};

export const toggleNoticesEnabled = () => (dispatch, getState) => {
	const noticesEnabled = !getState().community.noticesEnabled;

	ReactGA.event({
		category: 'Community',
		action: noticesEnabled ? 'Allow notices' : 'Disallow notices'
	});

	dispatch(actions.communitySetNoticesEnabled(noticesEnabled));

	putJson(`/api/community/${getState().community.uuid}`, {
		noticesEnabled
	}).catch(handleError(dispatch));
};

export const toggleNoticesAllowAll = () => (dispatch, getState) => {
	const noticesAllowAll = !getState().community.noticesAllowAll;
	dispatch(actions.communitySetNoticesAllowAll(noticesAllowAll));

	ReactGA.event({
		category: 'Community',
		action: noticesAllowAll ? 'Allow anyone to create notices' : 'Disallow anyone to create notices'
	});

	putJson(`/api/community/${getState().community.uuid}`, {
		noticesAllowAll
	}).catch(handleError(dispatch));
};

export const persistCommunityField = communityData => (dispatch, getState) => {
	dispatch(actions.communityUpdateField(communityData));

	ReactGA.event({
		category: 'Community',
		action: 'Profile edit'
	});

	const data = {...communityData};
	if (communityData.location) {
		// Don't save whole location object, just the suburbUuid, KTHX
		data.suburbUuid = communityData.location.suburbUuid;
		delete data.location;
	}

	putJson('/api/community/' + getState().community.uuid, data)
		.then(({uniqueName}) => {
			// Re-direct if uniqueName changes
			if (uniqueName) {
				dispatch(actions.communityUpdateField({profile: {uniqueName}}));
				dispatch(push('/!' + uniqueName));
			}
		})
		.catch(handleError(dispatch));
};

export const persistNewCommunity = communityData => (dispatch, getState) => {
	const data = {...communityData};

	ReactGA.event({
		category: 'Community',
		action: 'Create new'
	});

	if (data.location) {
		// Don't save whole location object, just the suburbUuid, KTHX
		data.suburbUuid = data.location.suburbUuid;
		delete data.location;
	}

	delete data.isNew;

	postJson('/api/community/', data)
		// Re-direct
		.then(community => dispatch(push('/!' + community.profile.uniqueName)))
		.catch(handleError(dispatch));
};

export const persistAbout = about => (dispatch, getState) => {
	dispatch(actions.communityUpdateAbout(about));

	putJson('/api/community/' + getState().community.uuid, {
		profile: {about}
	}).catch(handleError(dispatch));
};

export const persistBrief = brief => (dispatch, getState) => {
	dispatch(actions.communityUpdateBrief(brief));

	putJson('/api/community/' + getState().community.uuid, {
		profile: {brief}
	}).catch(handleError(dispatch));
};

export const persistName = name => (dispatch, getState) => {
	dispatch(actions.communityUpdateName(name));

	putJson('/api/community/' + getState().community.uuid, {name}).catch(handleError(dispatch));
};

export const persistContacts = () => (dispatch, getState) => {
	const {community} = getState();
	const contacts = community && community.editingContacts;

	dispatch(actions.communityCommitContacts(contacts));

	postJson(`/api/community/${community.uuid}/contact`, contacts).catch(handleError(dispatch));
};

export const persistCategory = category => (dispatch, getState) => {
	dispatch(actions.communityUpdateCategory(category));

	putJson('/api/community/' + getState().community.uuid, {category}).catch(handleError(dispatch));
};

export const persistLocation = suburbUuid => (dispatch, getState) => {
	const suburb = getState().location.suburbs.find(suburb => suburb.suburbUuid === suburbUuid);

	dispatch(actions.communityUpdateLocation(suburb));

	putJson('/api/community/' + getState().community.uuid, {suburbUuid}).catch(handleError(dispatch));
};

export const uploadBackgroundImage = image => (dispatch, getState) => {
	dispatch(actions.communityUpdatingBackgroundImage());
	const imageUuid = uuidv4();

	ReactGA.event({
		category: 'Community',
		action: 'Upload background'
	});

	postForm(`/api/community/${getState().community.uuid}/image/${imageUuid}`, {
		image
	})
		.then(() => dispatch(actions.communityUpdateBackgroundImage(imageUuid)))
		.catch(handleError(dispatch));
};

export const persistBackgroundImage = backgroundImage => (dispatch, getState) => {
	// Set the background image to an existing image.
	dispatch(actions.communityUpdateBackgroundImage(backgroundImage));

	putJson('/api/community/' + getState().community.uuid, {
		profile: {backgroundImage}
	}).catch(handleError(dispatch));
};

export const uploadProfileImage = image => (dispatch, getState) => {
	dispatch(actions.communityAddingProfileImage());
	const imageUuid = uuidv4();
	const priority = getState().community.profile.images.reduce(
		(max, val) => (max > val.priority ? max : val.priority),
		0
	);

	ReactGA.event({
		category: 'Community',
		action: 'Upload image'
	});

	postForm(`/api/community/${getState().community.uuid}/image/${imageUuid}?profile=true`, {image})
		.then(() =>
			dispatch(actions.communityAddedProfileImage({image: imageUuid, priority: priority + 1}))
		)
		.catch(handleError(dispatch));
};

export const deleteImage = imageUuid => (dispatch, getState) => {
	// Set the avatar image to an existing image.
	dispatch(actions.communityDeleteImage(imageUuid));

	ReactGA.event({
		category: 'Community',
		action: 'Delete image'
	});

	deleteJson(`/api/community/${getState().community.uuid}/image/${imageUuid}`).catch(
		handleError(dispatch)
	);
};

export const removeCommunity = uuid => (dispatch, getState) => {
	if (getState().community.uuid !== uuid) throw Error(`Community removal state mismatch ${uuid}`);
	dispatch(actions.communityRemove(uuid));
	dispatch(push('/'));

	deleteJson(`/api/community/${uuid}`).catch(handleError(dispatch));
};

export const muteCommunity = isMuted => (dispatch, getState) => {
	const {session, community} = getState();

	if (!community) throw Error(`Remove community`);
	if (!session || !session.current || !session.current.user)
		throw Error(`No current session when attempting to mute community`);

	dispatch(
		actions.communityMute({
			isMuted,
			userUuid: session.current.user.uuid
		})
	);

	postJson(`/api/community/${community.uuid}/${isMuted ? 'mute' : 'unmute'}`).catch(
		handleError(dispatch)
	);
};

export const inviteUser = uuid => (dispatch, getState) => {
	postJson(`/api/community/${getState().community.uuid}/invite`, [uuid])
		.then(() =>
			dispatch(uiActions.uiMessage(`Invitation to ${getState().community.name} has been sent`))
		)
		.catch(handleError(dispatch));
};

export const moveImage = (imageUuid, forward) => (dispatch, getState) => {
	putJson(`/api/community/${getState().community.uuid}/image/${imageUuid}`, {forward})
		.then(imgs => dispatch(actions.communitySetImgs(imgs)))
		.catch(handleError(dispatch));
};

export default {
	...actions,
	retrieveCommunity,
	joinCommunity,
	approveMembership,
	rejectMembership,
	removeMembership,
	toggleType,
	toggleNoticesEnabled,
	toggleNoticesAllowAll,
	persistCommunityField,
	persistAbout,
	persistBrief,
	persistName,
	persistContacts,
	persistCategory,
	persistLocation,
	persistBackgroundImage,
	uploadBackgroundImage,
	uploadProfileImage,
	deleteImage,
	persistNewCommunity,
	applyAdmin,
	removeCommunity,
	muteCommunity,
	inviteUser,
	moveImage
};



// WEBPACK FOOTER //
// ./src/community/community.actions.js